import { Card, CardContent, Typography, Stack, Box } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Label,
} from "recharts";
import { LicenseSummary } from "../types";
import { useTranslation } from "react-i18next";

interface LicenseDistributionChartProps {
  summary: LicenseSummary | null;
  loading?: boolean;
}

const COLORS = {
  active: "#2e7d32", // success.main (verde)
  expiring: "#ed6c02", // warning.main (laranja)
  expired: "#d32f2f", // error.main (vermelho)
  demo: "#0288d1", // info.main (azul claro)
};

import { useTheme } from "@mui/material/styles";

export default function LicenseDistributionChart({
  summary,
  loading,
}: LicenseDistributionChartProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (loading || !summary) {
    return (
      <Card sx={{ minWidth: 200, height: "100%" }}>
        <CardContent>
          <Typography color="text.secondary">
            {t("common.loading", "Loading...")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const data = [
    {
      name: t("metrics.licenseStatus.active", "Ativas"),
      value: Math.max(
        summary.activeLicenses - summary.expiringLicenses - summary.demoTenants,
        0
      ),
      color: COLORS.active,
    },
    {
      name: t("metrics.licenseStatus.expiring", "Expirando"),
      value: summary.expiringLicenses,
      color: COLORS.expiring,
    },
    {
      name: t("metrics.licenseStatus.expired", "Expiradas"),
      value: summary.expiredLicenses,
      color: COLORS.expired,
    },
    {
      name: t("metrics.licenseStatus.demo", "Demo"),
      value: summary.demoTenants,
      color: COLORS.demo,
    },
  ].filter((item) => item.value > 0);

  const total = summary.totalTenants;

  if (data.length === 0) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {t("dashboard.distribution.title", "Distribuição")}
              </Typography>
            </Box>
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                {t("dashboard.distribution.noData", "Nenhum tenant")}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <CardContent
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t("dashboard.distribution.title", "Distribuição")}
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <Label
                  value={total}
                  position="center"
                  offset={0}
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    fill: theme.palette.text.primary,
                  }}
                />
              </Pie>
              <Tooltip
                formatter={(value: any) => [`${value}`, ""]}
                contentStyle={{
                  borderRadius: 8,
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                itemStyle={{ color: "#333" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend Grid */}
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1}>
            {data.map((item, index) => {
              const percentage =
                total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
              return (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 0.5,
                    px: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: item.color,
                      }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {item.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" fontWeight={600}>
                      {item.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 35, textAlign: "right" }}
                    >
                      {percentage}%
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
